// The connection-epoch protocol (design D7) + keyframe/delta apply (D8). A monotonic
// epoch keeps exactly one socket active; per-state timeouts route failure to a
// backoff state (exponential + jitter, reset-on-stable); a `Rejected{upgrade}` is
// terminal; and a total trust boundary (D7 Table B / CF-AR3) closes the socket on any
// pre-`Welcome` data frame, duplicate `Welcome`, or malformed frame.
//
// One timer slot per connection: each state that carries a timeout (D7 "each
// pre-keyframe state carries a timeout") arms exactly one timer on entry, cleared
// before any new one is armed or on any state exit. Every socket callback is stamped
// with the epoch of the attempt that opened it and is a no-op once that epoch has
// been superseded — this is what makes overlapping connect() calls, and a stale
// backoff timer firing after a newer attempt has already stabilised, harmless
// (single-flight epoch ownership, D7).
//
// `Welcomed` is folded into the `Connecting -> Subscribed` transition: design D7
// note (1) says the send-`Subscribe` event fires *immediately* on `Welcome`, so the
// state is never observably rested in — `ConnState.Welcomed` is kept in the enum for
// completeness against the D7 state tables, but `state` goes straight from
// `Connecting` to `Subscribed`.

import { Tier } from './messages';
import type {
  BeeRecord,
  CellRecord,
  DanceRecord,
  BlobRecord,
  ClientMessage,
  PatchRecord,
  QueenRecord,
  RejectReason,
  ReplayControlMessage,
  RuinRecord,
  ServerMessage,
  Subscribe,
} from './messages';
import { dequantize, encodeClientMessage } from './codec';
import type { DecodeError } from './codec';

/** The nine D7 connection states (Table A/B row keys). */
export enum ConnState {
  Idle = 'idle',
  Connecting = 'connecting',
  Welcomed = 'welcomed',
  Subscribed = 'subscribed',
  Keyframed = 'keyframed',
  GapWait = 'gapWait',
  Backoff = 'backoff',
  Rejected = 'rejected',
  Closed = 'closed',
}

/** Monotonically increasing ownership token for one connection attempt. */
export type ConnectionEpoch = number;

/** The five client->server mutation messages `sendMutation` accepts (client-wire
 * "Outbound mutation send") — everything but the connection's own internal
 * `hello`/`subscribe`/`ack` traffic. */
export type MutationMessage = Extract<
  ClientMessage,
  { type: 'prompt' | 'handEdit' | 'rollback' | 'setBirthType' | 'createType' }
>;

/** The definite result of a `sendMutation` call: `sent` once encoded and written
 * to the live socket, `not-ready` if refused (nothing written, nothing queued). */
export type SendResult = 'sent' | 'not-ready';

/**
 * One live socket, owned by the connection. A real socket wraps a `WebSocket` and
 * decodes inbound frames to `ServerMessage` (emitting `onMalformed` on a decode
 * error); the mock transport (D14) feeds already-decoded messages directly, which is
 * exactly why it cannot prove the outbound encode/handshake path (CF1).
 */
export interface Transport {
  send(bytes: Uint8Array): void;
  close(): void;
}

/** Epoch-stamped callbacks the connection hands each transport it opens. */
export interface TransportHandlers {
  onOpen(): void;
  onMessage(msg: ServerMessage): void;
  onMalformed(err: DecodeError): void;
  onClose(): void;
}

export type TransportFactory = (handlers: TransportHandlers) => Transport;

/** Injectable timer seam so per-state timeouts and backoff are deterministically testable. */
export interface Scheduler {
  setTimer(fn: () => void, ms: number): number;
  clearTimer(id: number): void;
}

/** Injectable RNG seam ([0,1)) so backoff jitter is deterministic in tests. */
export interface Rng {
  next(): number;
}

/** The applied, confirmed-tier entity model the renderer reads (D8/D11). */
export interface WorldSnapshot {
  tier: Tier;
  tick: number;
  seq: bigint;
  bees: Map<number, BeeRecord>;
  blobs: Map<number, BlobRecord>;
  queens: Map<number, QueenRecord>;
  patches: Map<number, PatchRecord>;
  ruins: Map<number, RuinRecord>;
  /** The close-tier active dances (dance-visuals), keyed by `dancerId` in a namespace
   * distinct from `bees`, targets dequantized to world units. Empty at the wide tier. */
  dances: Map<number, DanceRecord>;
  /** The match-level dance audibility radius (world units, from the Welcome manifest) —
   * the renderer's watcher-derivation radius. */
  danceRadius: number;
  /** The close-tier hive cells (hive-egg-cells), keyed by cell id in a namespace distinct from
   * `bees`, positions dequantized to world units. The renderer composes the hive cell-by-cell
   * from this map. Empty at the wide tier; the keyframe carries the full set so a reconnect /
   * tier switch (which resets the whole snapshot) recovers it with no delta catch-up. */
  cells: Map<number, CellRecord>;
  /** The close-tier `CellPayload.truncated` cap bit (hive-egg-cells), carried from the close
   * keyframe (false at the wide tier / before any keyframe) — lets the renderer show an honest
   * "cells beyond here not shown" cue instead of a silently-holed hive. A close delta does not
   * re-report it (a keyframe-level property). */
  cellsTruncated: boolean;
  /** The close-tier `ClosePayload.truncated` cap bit, carried through from the close
   * keyframe (false at the wide tier / before any keyframe). Drives the renderer's
   * degradation trigger (pixel-art-pass implementation-review F12): a truncated payload
   * selects `reduced` animation mode. A close delta does not re-report it — the cap is a
   * keyframe-level property that persists across the deltas mutating the retained set. */
  truncated: boolean;
  /** The close-tier `ClosePayload.totalCount` (population before truncation), 0 at the wide
   * tier / before any keyframe. Feeds the degradation monitor's population-cap arm. */
  totalCount: number;
}

export interface ConnectionConfig {
  hello: { protocolVersion: number; sessionToken: string; matchId: string };
  handshakeTimeoutMs: number;
  firstKeyframeTimeoutMs: number;
  gapRecoveryTimeoutMs: number;
  backoffBaseMs: number;
  backoffCapMs: number;
}

export interface ConnectionDeps {
  transportFactory: TransportFactory;
  scheduler: Scheduler;
  rng: Rng;
  /** Supplies the current requested subscription for a `Subscribe` (from the LOD controller). */
  subscription(): Subscribe;
  /** Consulted on a keyframe: does its tier match the outstanding request? (D9) */
  keyframeDisposition(tier: Tier): 'confirm' | 'ignore';
}

export interface ConnectionCallbacks {
  onStateChange?(state: ConnState): void;
  onWorld?(world: WorldSnapshot): void;
  onServerMessage?(msg: ServerMessage): void;
  onMutationRejected?(reason: RejectReason): void;
  onUpgradeRequired?(minVersion: number): void;
}

function emptyWorld(tier: Tier, tick: number, seq: bigint): WorldSnapshot {
  return {
    tier,
    tick,
    seq,
    bees: new Map(),
    blobs: new Map(),
    queens: new Map(),
    patches: new Map(),
    ruins: new Map(),
    dances: new Map(),
    danceRadius: 40,
    cells: new Map(),
    cellsTruncated: false,
    truncated: false,
    totalCount: 0,
  };
}

const STEADY_TYPES = new Set<ServerMessage['type']>([
  'matchState',
  'colonyMeta',
  'promptStatus',
  'result',
  'stateHistogram',
]);

export class Connection {
  private _state: ConnState = ConnState.Idle;
  private _epoch: ConnectionEpoch = 0;
  private socket: Transport | null = null;
  private timerId: number | null = null;
  private backoffAttempt = 0;
  private lastAppliedSeq: bigint | null = null;
  private world: WorldSnapshot | null = null;
  /** From the Welcome manifest (always precedes any keyframe); drives `deq`. */
  private fieldSize = 4096;
  /** From the Welcome manifest (dance-visuals): the dance audibility radius carried onto
   * each `WorldSnapshot` for the renderer's watcher derivation. */
  private danceRadius = 40;

  constructor(
    private readonly config: ConnectionConfig,
    private readonly deps: ConnectionDeps,
    private readonly callbacks: ConnectionCallbacks = {},
  ) {}

  get state(): ConnState {
    return this._state;
  }

  get epoch(): ConnectionEpoch {
    return this._epoch;
  }

  connect(): void {
    this.openNewAttempt();
  }

  disconnect(): void {
    if (this._state === ConnState.Closed) {
      return;
    }
    this.clearTimer();
    this.teardownSocket();
    this.setState(ConnState.Closed);
  }

  /**
   * Encodes and sends a client->server mutation (`Prompt`/`HandEdit`/`Rollback`/
   * `SetBirthType`/`CreateType`) on the live socket, but only in a send-ready state
   * (`Subscribed`, `Keyframed`, or `GapWait` — a live socket exists). Epoch-safe by
   * construction: it writes to `this.socket`, which is always the current attempt's
   * socket (a superseded attempt's socket was already torn down and nulled out, so
   * there is nothing stale to accidentally target). Never opens, reconnects, or
   * tears down a socket — a send on the already-owned connection, consistent with
   * the internal `sendSubscribe`/`sendAck` discipline (client-wire "Outbound
   * mutation send").
   */
  sendMutation(msg: MutationMessage): SendResult {
    if (!this.isSendReady()) {
      return 'not-ready';
    }
    this.socket?.send(encodeClientMessage(msg));
    return 'sent';
  }

  /**
   * Encodes and sends a spectator-only `ReplayControl` (pause/resume/set-speed/restart)
   * on the live socket, in a send-ready state only. Same epoch-safe, never-open discipline
   * as `sendMutation` — a `ReplayControl` is a playback control, not a colony mutation, so
   * it rides its own method rather than the `MutationMessage` union (replay-viewer task
   * 10.6). The server re-checks current-connection + accepted-`Subscribe` before applying
   * any verb, so ordering/supersede races are the server's authority, not the client's.
   */
  sendReplayControl(control: ReplayControlMessage): SendResult {
    if (!this.isSendReady()) {
      return 'not-ready';
    }
    this.socket?.send(encodeClientMessage({ type: 'replayControl', value: control }));
    return 'sent';
  }

  private isSendReady(): boolean {
    return (
      this._state === ConnState.Subscribed ||
      this._state === ConnState.Keyframed ||
      this._state === ConnState.GapWait
    );
  }

  /** Notify the connection its subscription changed (viewport/tier) — Table A event. */
  subscriptionChanged(): void {
    switch (this._state) {
      case ConnState.Subscribed:
      case ConnState.Keyframed:
      case ConnState.GapWait:
        this.sendSubscribe();
        return;
      default:
        // Idle/Connecting/Backoff/Rejected/Closed: nothing live to (re)send to yet;
        // the initial Subscribe already reads the latest `deps.subscription()` when sent.
        return;
    }
  }

  // --- attempt lifecycle -----------------------------------------------------

  private openNewAttempt(): void {
    this.clearTimer();
    this.teardownSocket();
    this._epoch += 1;
    const epoch = this._epoch;
    this.lastAppliedSeq = null;
    this.world = null;
    this.setState(ConnState.Connecting);

    const handlers: TransportHandlers = {
      onOpen: () => this.handleOpen(epoch),
      onMessage: (msg) => this.handleMessage(epoch, msg),
      onMalformed: (err) => this.handleMalformed(epoch, err),
      onClose: () => this.handleClose(epoch),
    };
    this.socket = this.deps.transportFactory(handlers);
    this.armTimer(this.config.handshakeTimeoutMs, epoch, () => this.closeToBackoff());
  }

  private teardownSocket(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  private setState(next: ConnState): void {
    this._state = next;
    this.callbacks.onStateChange?.(next);
  }

  // --- timers ------------------------------------------------------------------

  private armTimer(ms: number, epoch: ConnectionEpoch, onFire: () => void): void {
    this.clearTimer();
    this.timerId = this.deps.scheduler.setTimer(() => {
      this.timerId = null;
      if (epoch !== this._epoch) {
        return; // superseded: this timer belonged to a dead attempt
      }
      onFire();
    }, ms);
  }

  private clearTimer(): void {
    if (this.timerId !== null) {
      this.deps.scheduler.clearTimer(this.timerId);
      this.timerId = null;
    }
  }

  // --- backoff -------------------------------------------------------------------

  private closeToBackoff(): void {
    this.teardownSocket();
    this.enterBackoff();
  }

  private enterBackoff(): void {
    this.clearTimer();
    this.setState(ConnState.Backoff);
    const capped = Math.min(
      this.config.backoffCapMs,
      this.config.backoffBaseMs * 2 ** this.backoffAttempt,
    );
    const delay = this.deps.rng.next() * capped;
    this.backoffAttempt += 1;
    const epoch = this._epoch;
    this.timerId = this.deps.scheduler.setTimer(() => {
      this.timerId = null;
      if (epoch !== this._epoch) {
        return; // a newer attempt has already superseded this scheduled reconnect
      }
      this.openNewAttempt();
    }, delay);
  }

  // --- outbound sends --------------------------------------------------------------

  private sendSubscribe(): void {
    const sub = this.deps.subscription();
    this.socket?.send(encodeClientMessage({ type: 'subscribe', value: sub }));
  }

  private sendAck(): void {
    if (this.lastAppliedSeq === null) {
      return;
    }
    this.socket?.send(
      encodeClientMessage({ type: 'ack', value: { highestContiguousSeq: this.lastAppliedSeq } }),
    );
  }

  // --- transport event handlers (epoch-guarded) -------------------------------------

  private handleOpen(epoch: ConnectionEpoch): void {
    if (epoch !== this._epoch || this._state !== ConnState.Connecting) {
      return;
    }
    this.socket?.send(encodeClientMessage({ type: 'hello', value: this.config.hello }));
  }

  private handleClose(epoch: ConnectionEpoch): void {
    if (epoch !== this._epoch) {
      return;
    }
    if (
      this._state === ConnState.Idle ||
      this._state === ConnState.Backoff ||
      this._state === ConnState.Rejected ||
      this._state === ConnState.Closed
    ) {
      return; // no live socket to have dropped
    }
    this.socket = null;
    this.enterBackoff();
  }

  private handleMalformed(epoch: ConnectionEpoch, _err: DecodeError): void {
    if (epoch !== this._epoch) {
      return;
    }
    if (
      this._state === ConnState.Idle ||
      this._state === ConnState.Backoff ||
      this._state === ConnState.Rejected ||
      this._state === ConnState.Closed
    ) {
      return; // no live socket
    }
    // Trust boundary: an undecodable/malformed frame in any connected state closes
    // the socket without acting on it, never continuing on a corrupt stream.
    this.closeToBackoff();
  }

  private handleMessage(epoch: ConnectionEpoch, msg: ServerMessage): void {
    if (epoch !== this._epoch) {
      return;
    }
    switch (this._state) {
      case ConnState.Connecting:
        this.handleMessageConnecting(msg);
        return;
      case ConnState.Subscribed:
        this.handleMessageSubscribed(msg);
        return;
      case ConnState.Keyframed:
        this.handleMessageKeyframed(msg);
        return;
      case ConnState.GapWait:
        this.handleMessageGapWait(msg);
        return;
      default:
        // Idle/Welcomed(unreachable)/Backoff/Rejected/Closed: no live socket, ignore.
        return;
    }
  }

  // --- per-state message handling (D7 Table B) --------------------------------------

  private handleMessageConnecting(msg: ServerMessage): void {
    switch (msg.type) {
      case 'welcome':
        this.onWelcome(msg);
        return;
      case 'rejected':
        if (msg.value.reason.kind === 'upgrade') {
          this.enterRejected(msg.value.reason.minVersion);
        } else if (msg.value.reason.kind === 'unauthorized') {
          this.enterClosed();
        } else {
          this.closeToBackoff();
        }
        return;
      default:
        // CF-AR3 trust boundary: any protocol data frame (keyframe/delta/steady)
        // arriving before Welcome is a protocol-ordering violation.
        this.closeToBackoff();
        return;
    }
  }

  private onWelcome(msg: Extract<ServerMessage, { type: 'welcome' }>): void {
    this.fieldSize = msg.value.manifest.fieldSize;
    this.danceRadius = msg.value.manifest.danceRadius;
    this.callbacks.onServerMessage?.(msg);
    // The send-Subscribe event fires immediately on Welcome (D7 note 1) — Welcomed
    // is never observably rested in.
    this.sendSubscribe();
    this.setState(ConnState.Subscribed);
    this.armTimer(this.config.firstKeyframeTimeoutMs, this._epoch, () => this.closeToBackoff());
  }

  private handleMessageSubscribed(msg: ServerMessage): void {
    switch (msg.type) {
      case 'welcome':
        this.closeToBackoff(); // duplicate Welcome
        return;
      case 'keyframe':
        if (this.deps.keyframeDisposition(msg.value.tier) === 'confirm') {
          this.enterKeyframed(msg);
        }
        return; // stale-tier keyframe: ignore, keep waiting
      case 'delta':
        return; // no base yet
      case 'rejected':
        this.handleRejectedPostHandshake(msg.value.reason);
        return;
      default:
        if (STEADY_TYPES.has(msg.type)) {
          this.callbacks.onServerMessage?.(msg);
        }
        return;
    }
  }

  private handleMessageKeyframed(msg: ServerMessage): void {
    switch (msg.type) {
      case 'welcome':
        this.closeToBackoff(); // duplicate Welcome
        return;
      case 'keyframe':
        if (this.deps.keyframeDisposition(msg.value.tier) === 'confirm') {
          this.enterKeyframed(msg); // re-key at that tier (recovery/confirm)
        }
        return;
      case 'delta':
        this.applyOrGap(msg);
        return;
      case 'rejected':
        this.handleRejectedPostHandshake(msg.value.reason);
        return;
      default:
        if (STEADY_TYPES.has(msg.type)) {
          this.callbacks.onServerMessage?.(msg);
        }
        return;
    }
  }

  private handleMessageGapWait(msg: ServerMessage): void {
    switch (msg.type) {
      case 'welcome':
        this.closeToBackoff(); // duplicate Welcome
        return;
      case 'keyframe':
        if (this.deps.keyframeDisposition(msg.value.tier) === 'confirm') {
          this.enterKeyframed(msg); // resync
        }
        return;
      case 'delta':
        return; // still waiting for the recovery keyframe
      case 'rejected':
        this.handleRejectedPostHandshake(msg.value.reason);
        return;
      default:
        if (STEADY_TYPES.has(msg.type)) {
          this.callbacks.onServerMessage?.(msg);
        }
        return;
    }
  }

  private handleRejectedPostHandshake(reason: RejectReason): void {
    if (reason.kind === 'upgrade') {
      this.enterRejected(reason.minVersion);
      return;
    }
    // A mutation Rejected{other} (unauthorized/rateLimited/stale/invalid) surfaces to
    // the UI and does not drop the stream — only Rejected{upgrade} is terminal.
    this.callbacks.onMutationRejected?.(reason);
  }

  // --- keyframe / delta apply (D8) -----------------------------------------------

  /** Wire coordinates are u16-quantized per the lod.rs contract
   * (`-fieldSize/2 -> 0`, `+fieldSize/2 -> 65535`); the held `WorldSnapshot` carries
   * WORLD-space positions — its consumers (projection, interpolation) assume world
   * units. `fieldSize` comes from the Welcome manifest, which always precedes any
   * keyframe (protocol ordering). A dequantize error (impossible for in-range u16
   * with a valid fieldSize) keeps the raw coordinate rather than dropping data. */
  private deq(pos: readonly [number, number]): [number, number] {
    return [
      dequantize(pos[0], this.fieldSize) ?? pos[0],
      dequantize(pos[1], this.fieldSize) ?? pos[1],
    ];
  }

  private enterKeyframed(msg: Extract<ServerMessage, { type: 'keyframe' }>): void {
    this.clearTimer();
    const kf = msg.value;
    const world = emptyWorld(kf.tier, kf.tick, kf.seq);
    if (kf.tier === Tier.Wide) {
      for (const b of kf.blobs) world.blobs.set(b.blobId, { ...b, centroid: this.deq(b.centroid) });
      for (const q of kf.queens) world.queens.set(q.id, { ...q, position: this.deq(q.position) });
      for (const p of kf.patches) world.patches.set(p.id, { ...p, position: this.deq(p.position) });
      for (const r of kf.ruins) world.ruins.set(r.id, { ...r, position: this.deq(r.position) });
    } else {
      for (const b of kf.bees.records) world.bees.set(b.id, { ...b, position: this.deq(b.position) });
      for (const p of kf.patches) world.patches.set(p.id, { ...p, position: this.deq(p.position) });
      // Dances (dance-visuals): keyed by dancerId, target dequantized to world units. The
      // keyframe carries the full active set, so a reconnect recovers it with no catch-up.
      for (const d of kf.dances)
        world.dances.set(d.dancerId, { ...d, target: this.deq(d.target) });
      world.danceRadius = this.danceRadius;
      // Hive cells (hive-egg-cells): keyed by cell id, positions dequantized. The keyframe
      // carries the full in-viewport set, so a reconnect / tier switch recovers the hive with
      // no delta catch-up; `emptyWorld` above already reset any stale cells (discontinuity reset).
      for (const c of kf.cells.records)
        world.cells.set(c.id, { ...c, position: this.deq(c.position) });
      world.cellsTruncated = kf.cells.truncated;
      // Carry the close cap pressure onto the snapshot (F12): the renderer's degradation
      // trigger reads `truncated`/`totalCount` from here, so a payload at `close_lod_cap`
      // proactively degrades to `reduced` mode. Deltas retain these (they never re-report the
      // cap), so they persist until the next keyframe.
      world.truncated = kf.bees.truncated;
      world.totalCount = kf.bees.totalCount;
    }
    this.world = world;
    this.lastAppliedSeq = kf.seq;
    this.backoffAttempt = 0; // stably keyframed: reset the backoff counter
    this.setState(ConnState.Keyframed);
    this.callbacks.onWorld?.(world);
  }

  private applyOrGap(msg: Extract<ServerMessage, { type: 'delta' }>): void {
    const delta = msg.value;
    if (this.world === null || delta.baseSeq !== this.lastAppliedSeq) {
      this.enterGapWait();
      return;
    }
    const world = this.world;
    if (delta.tier === Tier.Wide) {
      for (const up of delta.upserts) {
        switch (up.kind) {
          case 'blob':
            world.blobs.set(up.value.blobId, { ...up.value, centroid: this.deq(up.value.centroid) });
            break;
          case 'queen':
            world.queens.set(up.value.id, { ...up.value, position: this.deq(up.value.position) });
            break;
          case 'patch':
            world.patches.set(up.value.id, { ...up.value, position: this.deq(up.value.position) });
            break;
          case 'ruin':
            world.ruins.set(up.value.id, { ...up.value, position: this.deq(up.value.position) });
            break;
        }
      }
      for (const del of delta.deletes) {
        switch (del.kind) {
          case 'blob':
            world.blobs.delete(del.id);
            break;
          case 'queen':
            world.queens.delete(del.id);
            break;
          case 'patch':
            world.patches.delete(del.id);
            break;
          case 'ruin':
            world.ruins.delete(del.id);
            break;
        }
      }
    } else {
      for (const up of delta.upserts) {
        switch (up.kind) {
          case 'bee':
            world.bees.set(up.value.id, { ...up.value, position: this.deq(up.value.position) });
            break;
          case 'patch':
            world.patches.set(up.value.id, { ...up.value, position: this.deq(up.value.position) });
            break;
          case 'dance':
            // A dance entity, keyed by dancerId independently of the same-id bee.
            world.dances.set(up.value.dancerId, {
              ...up.value,
              target: this.deq(up.value.target),
            });
            break;
          case 'cell':
            // A cell entity, keyed by cell id independently of the same-id bee — a state change
            // (damage, occupancy, ruin) is a replacing upsert.
            world.cells.set(up.value.id, { ...up.value, position: this.deq(up.value.position) });
            break;
        }
      }
      for (const del of delta.deletes) {
        switch (del.kind) {
          case 'bee':
            world.bees.delete(del.id);
            break;
          case 'patch':
            world.patches.delete(del.id);
            break;
          case 'dance':
            world.dances.delete(del.id);
            break;
          case 'cell':
            world.cells.delete(del.id);
            break;
        }
      }
    }
    world.seq = delta.seq;
    world.tick = delta.tick;
    this.lastAppliedSeq = delta.seq;
    this.backoffAttempt = 0; // reset backoff once stably applying deltas
    this.callbacks.onWorld?.(world);
    this.sendAck();
  }

  private enterGapWait(): void {
    this.setState(ConnState.GapWait);
    this.armTimer(this.config.gapRecoveryTimeoutMs, this._epoch, () => this.closeToBackoff());
  }

  // --- terminal states -------------------------------------------------------------

  private enterRejected(minVersion: number): void {
    this.clearTimer();
    this.teardownSocket();
    this.setState(ConnState.Rejected);
    this.callbacks.onUpgradeRequired?.(minVersion);
  }

  private enterClosed(): void {
    this.clearTimer();
    this.teardownSocket();
    this.setState(ConnState.Closed);
  }
}
